/**
 * View model for the Agent Autonomy settings page: one discriminated-union
 * status, the derived slider level/coverage, and stable handlers whose every
 * write is exactly ONE `propertiesUpdated` event.
 *
 * Derivation lives in ../autonomyPresets.ts (pure, unit-tested); storage
 * access lives in ./useAgentSettings.ts. This hook only composes the two and
 * owns the transient "pending" state a slider needs while a write is in
 * flight.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  CAPABILITY_LABELS,
  CONTACTS_SCOPE_LABEL,
  CONTACTS_VERB_LABELS,
  COVERAGE_ALL_PREFIX,
  COVERAGE_CONTACTS_OFF_SUFFIX,
  COVERAGE_CONTACTS_ONLY,
  COVERAGE_NONE,
  COVERAGE_PIPELINES_ONLY_PREFIX,
  joinWithAnd,
  lowerFirst,
  PIPELINE_VERB_LABELS,
  STOP_LABELS,
  TOAST_ACTIVE_RESUMING_PREFIX,
  TOAST_ACTIVE_SET_PREFIX,
  TOAST_AT,
  TOAST_FOR,
  TOAST_OFF,
  TOAST_ON,
  TOAST_PAUSED,
  TOAST_RESET_PREFIX,
  TOAST_SET_TO_PREFIX,
  TOAST_UNDO,
} from '../agent-autonomy-copy';
import type {
  AutonomyDerivation,
  AutonomyGrantState,
  AutonomyStop,
  CapabilityKey,
  ContactsVerb,
  PipelineVerb,
} from '../autonomyPresets';
import {
  applyCapability,
  applyLevel,
  DEFAULT_AUTONOMY_STOP,
  deriveAutonomy,
  deriveCapabilityCoverage,
  matrixForStop,
  setContactsScope,
  setPipelineScope,
  toWireProps,
} from '../autonomyPresets';
import type { AgentCaseType } from './useAgentSettings';
import { useAgentSettings } from './useAgentSettings';

export type AgentAutonomyStatus = 'loading' | 'absent' | 'error' | 'ready';

export interface AgentAutonomyView {
  status: AgentAutonomyStatus;
  enabled: boolean;
  /** The derived level, or the stop a slider write is heading to while it is
   *  in flight (so the thumb does not snap back mid-save). */
  displayedLevel: AutonomyStop;
  derived: AutonomyDerivation;
  /** One sentence describing where the agent may act. */
  coverageLine: string;
  caseTypes: AgentCaseType[];
  grants: AutonomyGrantState;
  isSaving: boolean;
  retry(): void;
  setActive(on: boolean): void;
  setLevel(stop: AutonomyStop): void;
  resetToLevel(): void;
  /** One capability on the ladder, across every scope it applies to. */
  setCapability(key: CapabilityKey, on: boolean): void;
  setContactsScope(on: boolean): void;
  setPipelineScope(entityTypeName: string, on: boolean): void;
  setContactsVerb(verb: ContactsVerb, on: boolean): void;
  setPipelineVerb(
    entityTypeName: string,
    verb: PipelineVerb,
    on: boolean
  ): void;
}

/** "all 3 pipelines" / "Deals and Tasks" / "2 of 3 pipelines". */
function pipelinesPhrase(derived: AutonomyDerivation): string {
  const granted = derived.grantedPipelines;
  const total = granted.length + derived.missingPipelines.length;
  if (granted.length === 0) {
    return '';
  }
  if (derived.missingPipelines.length === 0) {
    return granted.length <= 2
      ? joinWithAnd(granted.map(t => t.label))
      : `all ${granted.length} pipelines`;
  }
  return `${granted.length} of ${total} pipelines`;
}

/** Scope labels inside a sentence: the contacts label lowercased, pipeline
 *  labels (customer text) as written. */
function scopeLabelsPhrase(labels: readonly string[]): string {
  return joinWithAnd(
    labels.map(label =>
      label === CONTACTS_SCOPE_LABEL ? lowerFirst(label) : label
    )
  );
}

/** "Merge duplicates – on for Deals" */
function onOffPhrase(
  subject: string,
  on: boolean,
  where: string | null
): string {
  const state = on ? TOAST_ON : TOAST_OFF;
  return where
    ? `${subject} – ${state} ${TOAST_FOR} ${where}`
    : `${subject} – ${state}`;
}

export function describeCoverage(derived: AutonomyDerivation): string {
  if (derived.coverage === 'none') {
    return COVERAGE_NONE;
  }
  const pipelines = pipelinesPhrase(derived);
  if (derived.contactsGranted) {
    return pipelines
      ? `${COVERAGE_ALL_PREFIX} ${pipelines}.`
      : COVERAGE_CONTACTS_ONLY;
  }
  return `${COVERAGE_PIPELINES_ONLY_PREFIX} ${pipelines} – ${COVERAGE_CONTACTS_OFF_SUFFIX}.`;
}

function scopesPhrase(state: AutonomyGrantState, caseTypes: AgentCaseType[]) {
  const derived = deriveAutonomy(state, caseTypes);
  const pipelines = pipelinesPhrase(derived);
  if (derived.contactsGranted && pipelines) {
    return `contacts & companies and ${pipelines}`;
  }
  if (derived.contactsGranted) {
    return 'contacts & companies';
  }
  return pipelines;
}

export function useAgentAutonomyView(): AgentAutonomyView {
  const settings = useAgentSettings();
  const {
    enabled,
    addContacts,
    mergeContacts,
    assignContacts,
    editContacts,
    pipelineGrants,
    caseTypes,
    isLoading,
    isAbsent,
    hasError,
    isSaving,
    saveSettings,
    saveEnabled,
    saveAddContacts,
    saveMergeContacts,
    saveAssignContacts,
    saveEditContacts,
    savePipelineGrants,
    retry,
  } = settings;

  const [pendingLevel, setPendingLevel] = useState<AutonomyStop | null>(null);

  const grants = useMemo<AutonomyGrantState>(
    () => ({
      addContacts,
      mergeContacts,
      assignContacts,
      editContacts,
      pipelineGrants,
    }),
    [addContacts, mergeContacts, assignContacts, editContacts, pipelineGrants]
  );

  const derived = useMemo(
    () => deriveAutonomy(grants, caseTypes),
    [grants, caseTypes]
  );

  const coverageLine = useMemo(() => describeCoverage(derived), [derived]);

  // hasError is checked before isAbsent: a failed user/agent fetch leaves us
  // unsure of the real state, and it can coincide with isAbsent (an errored
  // query reports not-loading with no object). Surfacing the error first tells
  // the user the fetch failed rather than the misleading "no agent" state. Both
  // the error and absent branches render a Retry that calls `retry()`, so
  // ordering them this way never costs the user the refetch path.
  let status: AgentAutonomyStatus = 'ready';
  if (hasError) {
    status = 'error';
  } else if (isAbsent) {
    status = 'absent';
  } else if (isLoading) {
    status = 'loading';
  }

  const writeMatrix = useCallback(
    (next: AutonomyGrantState) => saveSettings(toWireProps(next, caseTypes)),
    [saveSettings, caseTypes]
  );

  const setActive = useCallback(
    (on: boolean) => {
      if (!on) {
        saveEnabled(false)
          .then(() => toast.success(TOAST_PAUSED))
          .catch(() => {
            // Error toast is raised by useAgentSettings.
          });
        return;
      }
      if (derived.coverage === 'none') {
        // First enable from nothing granted: apply the default stop across
        // every scope in the same event as the enable (owner ruling:
        // defaults on). A partial write could enable an agent that grants
        // nothing, or grant without enabling — one event avoids both.
        const matrix = matrixForStop(caseTypes, DEFAULT_AUTONOMY_STOP);
        saveSettings({
          ahoy_enabled: 'true',
          ...toWireProps(matrix, caseTypes),
        })
          .then(() =>
            toast.success(
              `${TOAST_ACTIVE_SET_PREFIX} ${STOP_LABELS[DEFAULT_AUTONOMY_STOP]} for ${scopesPhrase(matrix, caseTypes)}`
            )
          )
          .catch(() => {
            // Error toast is raised by useAgentSettings.
          });
        return;
      }
      saveEnabled(true)
        .then(() =>
          toast.success(
            `${TOAST_ACTIVE_RESUMING_PREFIX} ${STOP_LABELS[derived.level]}${derived.customized ? ' (customized)' : ''}`
          )
        )
        .catch(() => {
          // Error toast is raised by useAgentSettings.
        });
    },
    [saveEnabled, saveSettings, derived, caseTypes]
  );

  const setLevel = useCallback(
    (stop: AutonomyStop) => {
      if (stop === derived.level && !derived.customized) {
        return;
      }
      const snapshot = grants;
      const next = applyLevel(grants, caseTypes, stop);
      setPendingLevel(stop);
      writeMatrix(next)
        .then(() => {
          const where = scopesPhrase(next, caseTypes);
          toast.success(
            where
              ? `${TOAST_SET_TO_PREFIX} ${STOP_LABELS[stop]} for ${where}`
              : `${TOAST_SET_TO_PREFIX} ${STOP_LABELS[stop]}`,
            {
              action: {
                label: TOAST_UNDO,
                onClick: () => {
                  writeMatrix(snapshot).catch(() => {
                    // Error toast is raised by useAgentSettings.
                  });
                },
              },
            }
          );
        })
        .catch(() => {
          // Error toast is raised by useAgentSettings; the thumb reverts to
          // the derived level once pending clears.
        })
        .finally(() => setPendingLevel(null));
    },
    [derived, grants, caseTypes, writeMatrix]
  );

  // Every write below names what changed and where, and offers Undo — a
  // misclick on a setting that takes effect immediately should cost one more
  // click, not a hunt through the panel. Undo restores the full matrix as it
  // was when the write was made.
  const commitWithUndo = useCallback(
    (write: () => Promise<void>, message: string) => {
      const snapshot = grants;
      write()
        .then(() => {
          toast.success(message, {
            action: {
              label: TOAST_UNDO,
              onClick: () => {
                writeMatrix(snapshot).catch(() => {
                  // Error toast is raised by useAgentSettings.
                });
              },
            },
          });
        })
        .catch(() => {
          // Error toast is raised by useAgentSettings.
        });
    },
    [grants, writeMatrix]
  );

  const resetToLevel = useCallback(() => {
    if (!derived.customized) {
      return;
    }
    commitWithUndo(
      () => writeMatrix(applyLevel(grants, caseTypes, derived.level)),
      `${TOAST_RESET_PREFIX} ${STOP_LABELS[derived.level]}`
    );
  }, [derived, grants, caseTypes, writeMatrix, commitWithUndo]);

  const setCapability = useCallback(
    (key: CapabilityKey, on: boolean) => {
      const next = applyCapability(grants, caseTypes, key, on);
      if (next === grants) {
        return;
      }
      // Name the scopes the capability now covers (on) or covered (off).
      const { onLabels } = deriveCapabilityCoverage(
        on ? next : grants,
        caseTypes,
        CONTACTS_SCOPE_LABEL
      )[key];
      commitWithUndo(
        () => writeMatrix(next),
        onOffPhrase(
          CAPABILITY_LABELS[key],
          on,
          onLabels.length > 0 ? scopeLabelsPhrase(onLabels) : null
        )
      );
    },
    [grants, caseTypes, writeMatrix, commitWithUndo]
  );

  const setContactsScopeHandler = useCallback(
    (on: boolean) => {
      if (!on) {
        commitWithUndo(
          () => saveAddContacts(false),
          onOffPhrase(CONTACTS_SCOPE_LABEL, false, null)
        );
        return;
      }
      const next = setContactsScope(grants, true, derived.level);
      commitWithUndo(
        () =>
          saveSettings({
            ahoy_add_contacts: 'true',
            ahoy_merge_contacts: next.mergeContacts ? 'true' : 'false',
            ahoy_assign_contacts: next.assignContacts ? 'true' : 'false',
            ahoy_edit_contacts: next.editContacts ? 'true' : 'false',
          }),
        `${CONTACTS_SCOPE_LABEL} – ${TOAST_ON} ${TOAST_AT} ${STOP_LABELS[deriveAutonomy(next, caseTypes).level]}`
      );
    },
    [
      grants,
      derived.level,
      caseTypes,
      saveAddContacts,
      saveSettings,
      commitWithUndo,
    ]
  );

  const setPipelineScopeHandler = useCallback(
    (entityTypeName: string, on: boolean) => {
      const next = setPipelineScope(grants, entityTypeName, on, derived.level);
      const label =
        caseTypes.find(t => t.entityTypeName === entityTypeName)?.label ??
        entityTypeName;
      commitWithUndo(
        () =>
          savePipelineGrants(
            caseTypes
              .filter(t => t.entityTypeName in next.pipelineGrants)
              .map(t => ({
                entityTypeName: t.entityTypeName,
                ...next.pipelineGrants[t.entityTypeName],
              }))
          ),
        on
          ? `${label} – ${TOAST_ON} ${TOAST_AT} ${STOP_LABELS[deriveAutonomy(next, caseTypes).level]}`
          : onOffPhrase(label, false, null)
      );
    },
    [grants, derived.level, caseTypes, savePipelineGrants, commitWithUndo]
  );

  const setContactsVerb = useCallback(
    (verb: ContactsVerb, on: boolean) => {
      const saversByVerb: Record<ContactsVerb, (on: boolean) => Promise<void>> =
        {
          mergeContacts: saveMergeContacts,
          assignContacts: saveAssignContacts,
          editContacts: saveEditContacts,
        };
      commitWithUndo(
        () => saversByVerb[verb](on),
        onOffPhrase(
          CONTACTS_VERB_LABELS[verb],
          on,
          lowerFirst(CONTACTS_SCOPE_LABEL)
        )
      );
    },
    [saveMergeContacts, saveAssignContacts, saveEditContacts, commitWithUndo]
  );

  const setPipelineVerb = useCallback(
    (entityTypeName: string, verb: PipelineVerb, on: boolean) => {
      const label =
        caseTypes.find(t => t.entityTypeName === entityTypeName)?.label ??
        entityTypeName;
      commitWithUndo(
        () =>
          savePipelineGrants(
            caseTypes
              .filter(t => t.entityTypeName in grants.pipelineGrants)
              .map(t => {
                const grant = grants.pipelineGrants[t.entityTypeName];
                return {
                  entityTypeName: t.entityTypeName,
                  ...grant,
                  ...(t.entityTypeName === entityTypeName
                    ? { [verb]: on }
                    : {}),
                };
              })
          ),
        onOffPhrase(PIPELINE_VERB_LABELS[verb], on, label)
      );
    },
    [grants, caseTypes, savePipelineGrants, commitWithUndo]
  );

  return {
    status,
    enabled,
    displayedLevel: pendingLevel ?? derived.level,
    derived,
    coverageLine,
    caseTypes,
    grants,
    isSaving,
    retry,
    setActive,
    setLevel,
    resetToLevel,
    setCapability,
    setContactsScope: setContactsScopeHandler,
    setPipelineScope: setPipelineScopeHandler,
    setContactsVerb,
    setPipelineVerb,
  };
}
